import { useCallback } from 'react';

import type { CodeBlockSuggestion } from '@aline/common';
import { AlineTransforms, getIsPdfLocationAnchor } from '@aline/common';
import { getReferenceFromSuggestion } from 'src/components/ai/chat/ChatPanelMarkdown/helpers/getReference';
import { parseFileLocationId } from 'src/components/ai/chat/ChatPanelMarkdown/helpers/parseLocation';
import { useScrollToLocationForReference } from 'src/editor';
import { useEditorSuggestionState } from 'src/editor/editor-suggestion-state-registry';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { useMaybeDocumentParams } from 'src/helpers';
import { useMaybeEditor } from 'src/layout-engine';
import type { AlineClientEditor } from 'src/layout-engine';

import { getNextSuggestionUnit, getPreviousSuggestionUnit } from './suggestion-navigation-helpers';
import type { SuggestionUnit } from '../suggestion-units';
import { getSuggestionUnitRange } from '../suggestion-units';

/**
 * Hook for navigating to a suggestion from the chat panel.
 */
export function useSuggestionGroupNavigationHooks() {
  const editor = useMaybeEditor();
  const { documentId: openDocumentId } = useMaybeDocumentParams();
  const { setSelectedSuggestionGroupId, suggestionUnits } = useEditorSuggestionState();
  const { scrollToLocationForReference } = useScrollToLocationForReference();

  const windowHeight = useEditorTabStateSelector(state => state.reactWindow.windowHeight);
  const scrollOffset = useEditorTabStateSelector(state => state.reactWindow.scrollOffset);

  const handleNavigateToGroupBySuggestion = useCallback(
    (suggestion: CodeBlockSuggestion) => {
      if (!editor) return;

      const unit = suggestionUnits.find(u => u.groupId === suggestion.groupId);
      if (!unit) {
        const aiReference = getReferenceFromSuggestion(editor, suggestion);
        if (aiReference) scrollToLocationForReference({ aiReference });

        return;
      }

      setSelectedSuggestionGroupId(suggestion.groupId);
      navigateToSuggestionUnit(editor, unit, windowHeight, scrollOffset);
    },
    [
      editor,
      scrollOffset,
      scrollToLocationForReference,
      setSelectedSuggestionGroupId,
      suggestionUnits,
      windowHeight,
    ]
  );

  const handleNavigateToRedlineAction = useCallback(
    ({
      documentId,
      groupId,
      locationAnchor,
    }: {
      documentId: string;
      groupId: string;
      locationAnchor: string;
    }) => {
      /**
       * A redline anchor is only meaningful within its own document, so bail
       * when that document isn't the one currently open. This stops a PDF click
       * from scrolling/highlighting an unrelated document that happens to be in
       * view. (The Aline branch below is already scoped to the open editor's
       * suggestions; this guards the coordinate-based PDF branch too.)
       */
      if (documentId !== openDocumentId) return;

      /**
       * PDF documents render in an Apryse viewer, not a Slate editor, so there
       * is no in-document suggestion to navigate to (the edit is never applied
       * to a read-only PDF). Highlight the source paragraph by its coordinate
       * anchor instead — the same path citations use to scroll to and
       * highlight PDF text.
       */
      if (getIsPdfLocationAnchor(locationAnchor)) {
        const aiReference = parseFileLocationId(locationAnchor, '');
        if (aiReference) scrollToLocationForReference({ aiReference });
        return;
      }

      if (!editor) return;

      const unit = suggestionUnits.find(u => u.groupId === groupId);
      if (!unit) return;

      setSelectedSuggestionGroupId(groupId);
      navigateToSuggestionUnit(editor, unit, windowHeight, scrollOffset);
    },
    [
      editor,
      openDocumentId,
      scrollToLocationForReference,
      setSelectedSuggestionGroupId,
      suggestionUnits,
      windowHeight,
      scrollOffset,
    ]
  );

  return {
    handleNavigateToGroupBySuggestion,
    handleNavigateToRedlineAction,
  };
}

/**
 * Selects and scrolls to a suggestion unit's range: the exact marker range
 * for in-paragraph edits, the paragraph span of the group's sourced edits
 * for block suggestions.
 */
export function navigateToSuggestionUnit(
  editor: AlineClientEditor,
  unit: SuggestionUnit,
  windowHeight: number,
  scrollOffset: number
): void {
  const range = getSuggestionUnitRange(editor, unit);
  if (!range) return;

  AlineTransforms.select(editor, range);
  ClientTransforms.scrollToLocation(editor, {
    cMLocation: range,
    rWHeightPx: windowHeight,
    rWOffsetPx: scrollOffset,
    select: true,
  });
}

export function useSuggestionUnitNavigationHooks() {
  const editor = useMaybeEditor();
  const { selectedSuggestionGroupId, setSelectedSuggestionGroupId, suggestionUnits } =
    useEditorSuggestionState();

  const windowHeight = useEditorTabStateSelector(state => state.reactWindow.windowHeight);
  const scrollOffset = useEditorTabStateSelector(state => state.reactWindow.scrollOffset);

  const handleNavigateDown = useCallback(() => {
    if (!editor) return;

    const unit = getNextSuggestionUnit(suggestionUnits, selectedSuggestionGroupId, editor);
    if (!unit) return;

    setSelectedSuggestionGroupId(unit.groupId);

    navigateToSuggestionUnit(editor, unit, windowHeight, scrollOffset);
  }, [
    editor,
    selectedSuggestionGroupId,
    setSelectedSuggestionGroupId,
    suggestionUnits,
    windowHeight,
    scrollOffset,
  ]);

  const handleNavigateDownAfterRemovingCurrent = useCallback(() => {
    if (!editor) return;

    const unit = getNextSuggestionUnit(suggestionUnits, selectedSuggestionGroupId, editor);
    setSelectedSuggestionGroupId(unit?.groupId ?? null);

    if (!unit) return;

    navigateToSuggestionUnit(editor, unit, windowHeight, scrollOffset);
  }, [
    editor,
    selectedSuggestionGroupId,
    setSelectedSuggestionGroupId,
    suggestionUnits,
    windowHeight,
    scrollOffset,
  ]);

  const handleNavigateUp = useCallback(() => {
    if (!editor) return;

    const unit = getPreviousSuggestionUnit(suggestionUnits, selectedSuggestionGroupId, editor);
    if (!unit) return;

    setSelectedSuggestionGroupId(unit.groupId);
    navigateToSuggestionUnit(editor, unit, windowHeight, scrollOffset);
  }, [
    editor,
    selectedSuggestionGroupId,
    setSelectedSuggestionGroupId,
    suggestionUnits,
    windowHeight,
    scrollOffset,
  ]);

  return {
    handleNavigateDown,
    handleNavigateDownAfterRemovingCurrent,
    handleNavigateUp,
    selectedSuggestionGroupId,
    suggestionUnits,
  };
}

export function useSelectedSuggestionUnit() {
  const { selectedSuggestionGroupId, suggestionUnits } = useEditorSuggestionState();
  return {
    selectedSuggestionUnit:
      suggestionUnits.find(unit => unit.groupId === selectedSuggestionGroupId) ?? null,
    suggestionUnits,
  };
}
